import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {TeamMember} from '../../domain/entities/Pipeline.ts'
import {
    CONVERSION_GROUPS,
    CONVERSION_LOST_STAGE_REASON,
    conversionAccepts,
    ConversionBoardFilters,
    ConversionCard as ConversionCardData,
    ConversionColumn,
    ConversionGroup,
    ConversionStage,
    isFinalConversionStage,
} from '../../domain/entities/ConversionPipeline.ts'
import {useConversionBoard} from '../../hooks/usePipelines.ts'
import {BoardColumn} from './BoardColumn.tsx'
import {BoardFiltersBar} from './BoardFiltersBar.tsx'
import {ConversionCard} from './ConversionCard.tsx'
import {CardMenuItem} from './PipelineParts.tsx'
import {useBoardDrag} from './useBoardDrag.ts'

type ConversionBoardProps = {
    filters: ConversionBoardFilters
    onFiltersChange: (filters: ConversionBoardFilters) => void
    team: TeamMember[]
    selectedId: string | null
    onOpenCard: (card: ConversionCardData) => void
    // A drop on (or "Move to…" into) a manual column: the container sends that
    // column's command, after a form when it needs data.
    onRequestMove: (card: ConversionCardData, stage: ConversionStage) => void
    onRequestMoveTo: (card: ConversionCardData) => void
    onRequestOverride: (card: ConversionCardData) => void
    onRequestLost: (card: ConversionCardData) => void
}

const emptyColumn = (stage: ConversionStage, group: ConversionGroup): ConversionColumn => ({
    stage, group, type: 'EVENT', actor: null, final: false, drop: null, count: 0, cards: [], nextCursor: null,
})

// Conversion board: 24 columns in 6 collapsible groups. Only the manual
// columns take a dropped card; the rest return it with "Stages update
// automatically".
export const ConversionBoard: React.FC<ConversionBoardProps> = ({
    filters, onFiltersChange, team, selectedId, onOpenCard, onRequestMove, onRequestMoveTo, onRequestOverride, onRequestLost,
}) => {
    const {t} = useTranslation('dashboard')
    const {board, isLoadingBoard, isFetchingBoard, boardError, refetchBoard} = useConversionBoard(filters)
    const [collapsed, setCollapsed] = useState<Partial<Record<ConversionStage, boolean>>>({})
    const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<ConversionGroup, boolean>>>({})

    const columnOf = (stage: ConversionStage) => board?.columns.find((c) => c.stage === stage)
    const groups = CONVERSION_GROUPS
        .filter((g) => !filters.group || g.id === filters.group)
        .map((g) => {
            const columns = g.stages.map((stage) => columnOf(stage) ?? emptyColumn(stage, g.id))
            const count = board?.groups?.find((bg) => bg.id === g.id)?.count ?? columns.reduce((sum, c) => sum + c.count, 0)
            return {id: g.id, columns, count}
        })
    const total = groups.reduce((sum, g) => sum + g.count, 0)

    const {dragging, startDrag, endDrag, columnDragProps} = useBoardDrag<ConversionCardData, ConversionStage>({
        stageOf: (card) => card.crmStage,
        accepts: (card, stage) => conversionAccepts(card, columnOf(stage)),
        onDrop: onRequestMove,
        onRefused: (card, stage) => {
            const manual = !!columnOf(stage)?.drop
            enqueueSnackbar(manual
                ? t('pipelines.board.notFromStage', {stage: t(`pipelines.stage.${card.crmStage}`)})
                : t('pipelines.board.autoStages'), {variant: 'info'})
        },
    })

    const menuItems = (card: ConversionCardData): CardMenuItem[] => isFinalConversionStage(card.crmStage)
        ? [{label: t('pipelines.card.override'), onSelect: () => onRequestOverride(card)}]
        : [
            {label: t('pipelines.card.moveTo'), onSelect: () => onRequestMoveTo(card)},
            {label: t('pipelines.card.override'), onSelect: () => onRequestOverride(card)},
            {label: t('pipelines.card.moveToLostShort'), onSelect: () => onRequestLost(card)},
        ]

    const toggleGroup = (id: ConversionGroup) => setCollapsedGroups((c) => ({...c, [id]: !c[id]}))

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-3.5">
            <div className="flex items-center gap-3 flex-wrap flex-none">
                <BoardFiltersBar pipeline="conversion" filters={filters} team={team} onChange={onFiltersChange}/>
                <span className="flex-1"/>
                <span className="text-[12.5px] text-neutralBlack-600" aria-live="polite">
                    {isFetchingBoard && !isLoadingBoard ? t('pipelines.board.refreshing') : t('pipelines.board.total', {count: total})}
                </span>
            </div>

            <div className="flex-1 min-h-0 flex bg-white border border-neutralBlack-200 rounded-2xl overflow-hidden">
                {boardError && !board ? (
                    <div role="alert" className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
                        <span className="text-[15px] font-semibold text-neutralBlack-900">{t('pipelines.board.errorTitle')}</span>
                        <p className="text-[13px] text-neutralBlack-600 max-w-sm">{t('pipelines.board.errorMessage')}</p>
                        <button type="button" onClick={() => refetchBoard()}
                                className="h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-700 transition-colors duration-200 ease-out">
                            {t('pipelines.retry')}
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0 flex gap-5 p-3.5 overflow-x-auto overflow-y-hidden items-stretch" aria-busy={isLoadingBoard}>
                        {groups.map((group) => {
                            const label = t(`pipelines.group.${group.id}`)
                            const isCollapsed = !!collapsedGroups[group.id]
                            return (
                                <section key={group.id} aria-label={t('pipelines.group.label', {name: label, count: group.count})}
                                         className="flex-none flex flex-col min-h-0 gap-2">
                                    <div className="flex items-center gap-2 h-6 flex-none">
                                        <button type="button" onClick={() => toggleGroup(group.id)} aria-expanded={!isCollapsed}
                                                aria-label={t(isCollapsed ? 'pipelines.group.expand' : 'pipelines.group.collapse', {name: label})}
                                                className="w-6 h-6 rounded-md flex items-center justify-center text-neutralBlack-600 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                                            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                {isCollapsed ? <path d="m9 6 6 6-6 6"/> : <path d="m6 9 6 6 6-6"/>}
                                            </svg>
                                        </button>
                                        <span className="text-[11px] font-semibold tracking-[.08em] uppercase text-neutralBlack-600 whitespace-nowrap">{label}</span>
                                        <span className="text-[11px] font-medium text-neutralBlack-600">{group.count}</span>
                                        {!isCollapsed && <span aria-hidden="true" className="flex-1 h-px bg-neutralBlack-100 min-w-3"/>}
                                    </div>
                                    <div className="flex-1 min-h-0 flex gap-3">
                                        {isCollapsed ? (
                                            <button type="button" onClick={() => toggleGroup(group.id)} aria-label={t('pipelines.group.expand', {name: label})}
                                                    className="w-[52px] flex-none rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 flex flex-col items-center gap-3.5 py-3.5 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                                                <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-neutralBlack-200 text-neutralBlack-700 text-[11.5px] font-semibold inline-flex items-center justify-center">{group.count}</span>
                                                <span className="[writing-mode:vertical-rl] text-[12.5px] font-semibold whitespace-nowrap text-neutralBlack-700">{label}</span>
                                            </button>
                                        ) : isLoadingBoard ? (
                                            group.columns.map((column) => (
                                                <div key={column.stage} className="flex-none w-[300px] rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 p-3 flex flex-col gap-2.5">
                                                    <div className="h-5 w-32 rounded bg-neutralBlack-200 animate-pulse"/>
                                                    {Array.from({length: 2}).map((_, i) => <div key={i} className="h-[150px] rounded-xl bg-white animate-pulse"/>)}
                                                </div>
                                            ))
                                        ) : group.columns.map((column) => (
                                            <BoardColumn<ConversionCardData> key={column.stage} column={column}
                                                         source={{pipeline: 'conversion', stage: column.stage, filters}}
                                                         success={column.stage === 'DELIVERED'}
                                                         dropHint={column.drop ? t(CONVERSION_LOST_STAGE_REASON[column.stage] ? 'pipelines.column.dropHint' : 'pipelines.column.dropMove') : null}
                                                         collapsed={!!collapsed[column.stage]}
                                                         onToggleCollapsed={() => setCollapsed((c) => ({...c, [column.stage]: !c[column.stage]}))}
                                                         renderCard={(card) => (
                                                             <ConversionCard card={card} team={team} menuItems={menuItems(card)}
                                                                             selected={card.id === selectedId} dragging={card.id === dragging?.id}
                                                                             onOpen={onOpenCard} onDragStart={startDrag} onDragEnd={endDrag}/>
                                                         )}
                                                         {...columnDragProps(column.stage)}/>
                                        ))}
                                    </div>
                                </section>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
